// @flow
import { API_TEAM } from "api/teamApi";
import ModalAddTeamUser from "components/Team/ModalAddTeamUser";
import { invalidateLoadUsers } from "queries/users/useLoadUsers";
import * as React from "react";
import { toast } from "react-toastify";
import type { User } from "types/user";
import Ui from "ui";
import Icons from "ui/Icons";
import { buildFullName } from "utils/helpers";
import { ZentakeContext } from "utils/ZentakeContext";

import TeamTable from "../sections/TeamTable";

function TeamTab(): React.Node {
  const { setAlertConfig } = React.useContext(ZentakeContext);

  const [searchTerm, setSearchTerm] = React.useState("");
  const [isAddingUser, setIsAddingUser] = React.useState(false);
  const [isEditingUser, setIsEditingUser] = React.useState(false);
  const [selectedUserToEdit, setSelectedUserToEdit] =
    React.useState<User | null>(null);

  const onShowAddUserModal = React.useCallback(() => {
    setIsAddingUser(true);
  }, []);
  const onCloseAddUserModal = React.useCallback(() => {
    setIsAddingUser(false);
  }, []);

  const onShowEditUserModal = React.useCallback((user: User) => {
    setSelectedUserToEdit(user);
    setIsEditingUser(true);
  }, []);

  const onCloseEditUserModal = React.useCallback(() => {
    setIsEditingUser(false);
    setSelectedUserToEdit(null);
  }, []);

  const onEditUser = React.useCallback(
    async (first_name, last_name, email, role, user) => {
      try {
        await API_TEAM.teamUserEdit(user.id, {
          first_name,
          last_name,
          role,
        });

        invalidateLoadUsers();
        onCloseEditUserModal();
        toast.success(
          `${
            buildFullName(first_name, last_name) ?? "User"
          } successfully edited`,
        );
        return true;
      } catch (error) {
        toast.error(error?.message ?? "Sorry, something went wrong");
        return false;
      }
    },
    [onCloseEditUserModal],
  );

  const deleteUser = React.useCallback(async (userId) => {
    await API_TEAM.teamUserDelete(userId);
    invalidateLoadUsers();
    toast.info("Member successfully removed");
  }, []);

  const onDeleteUserPress = React.useCallback(
    (userId) => {
      setAlertConfig({
        title: "Remove team member",
        description: "Are you sure you want to remove this user from the team?",
        buttons: [
          { title: "Cancel" },
          {
            title: "Delete",
            style: "destructive",
            onPress: () => deleteUser(userId),
          },
        ],
        canDismiss: false,
      });
    },
    [setAlertConfig, deleteUser],
  );

  const sendReminder = React.useCallback(async (userEmail) => {
    try {
      await API_TEAM.teamUserRemind(userEmail);
      toast.info(`Reminder sent to ${userEmail}`);
    } catch (error) {
      toast.error(error?.message ?? "Error sending reminder");
    }
  }, []);

  const onSendReminderClick = React.useCallback(
    (userId, userEmail) => {
      setAlertConfig({
        title: "Send email reminder",
        description: `Are you sure you want to send a reminder to ${userEmail}?`,
        buttons: [
          { title: "Cancel" },
          {
            title: "Send",
            onPress: () => sendReminder(userEmail),
          },
        ],
        canDismiss: false,
      });
    },
    [sendReminder, setAlertConfig],
  );

  return (
    <>
      <div className="px-4 pt-3 pb-4 bg-white rounded-b rounded-tr">
        <div className="sm:flex sm:space-x-4">
          <Ui.SearchBar
            className="flex-1 mt-2"
            onChange={setSearchTerm}
            value={searchTerm}
          />
          <Ui.PrimaryButton
            className="mt-2 space-x-2"
            onClick={onShowAddUserModal}
          >
            <Icons.UserAdd className="text-white" />
            <span>Add Member</span>
          </Ui.PrimaryButton>
        </div>

        <div className="mt-4 border border-gray-400 rounded overflow-x-auto">
          <TeamTable
            onDeleteUser={onDeleteUserPress}
            onEditUser={onShowEditUserModal}
            onSendReminder={onSendReminderClick}
            searchTerm={searchTerm}
          />
        </div>
      </div>
      {isAddingUser && <ModalAddTeamUser onClose={onCloseAddUserModal} />}
      {isEditingUser && (
        <ModalAddTeamUser
          isEditing
          onClose={onCloseEditUserModal}
          onSubmit={onEditUser}
          userToEdit={selectedUserToEdit}
        />
      )}
    </>
  );
}

export default TeamTab;
